import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth.service';
import { trainingsService, TrainingStats } from '../../services/trainings.service';
import StatsCard from '../../components/dashboard/StatsCard';
import { 
  AlertCircle
} from 'lucide-react';

interface TrainingCenterInfo {
  id: number;
  name: string;
  phone_number: string;
  about?: string;
  address?: string;
  website?: string;
  social_media_links?: string;
  field_ids?: number[];
  wilaya_id?: number;
}

interface ExtendedStats {
  trainingStats: TrainingStats;
  totalViews: number;
  averageRating: number;
  totalReviews: number;
  totalRevenue: number;
  pendingApprovals: number;
  activeSessions: number;
  completedSessions: number;
}

const StatsPage: React.FC = () => {
  const { user } = useAuth();
  const [trainingCenter, setTrainingCenter] = useState<TrainingCenterInfo | null>(null);
  const [stats, setStats] = useState<ExtendedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch training center info
        const centerInfo = await authService.getTrainingCenterInfo();
        setTrainingCenter(centerInfo);
        
        // Fetch all statistics
        const [trainingStats, dashboardStats, centerStats] = await Promise.all([
          trainingsService.getTrainingStats(),
          trainingsService.getDashboardStats().catch(() => null),
          trainingsService.getCenterStats().catch(() => null)
        ]);
        
        // Combine all statistics with real data
        const extendedStats: ExtendedStats = {
          trainingStats,
          totalViews: centerStats?.totalViews || 0,
          averageRating: centerStats?.averageRating || 0,
          totalReviews: centerStats?.totalReviews || 0,
          totalRevenue: dashboardStats?.totalRevenue || 0,
          pendingApprovals: centerStats?.pendingApprovals || 0,
          activeSessions: centerStats?.activeSessions || 0,
          completedSessions: centerStats?.completedSessions || 0,
        };
        
        setStats(extendedStats);
      } catch (err) {
        setError('Erreur lors du chargement des statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-6">
                  <div className="h-16 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">Erreur de chargement</h3>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Statistiques du Centre
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Vue d'ensemble des performances de {trainingCenter?.name}
          </p>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Total Trainings */}
          <StatsCard
            name="Formations Total"
            value={stats?.trainingStats.total || 0}
            change="+12%"
            changeType="positive"
            icon="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            color="blue"
          />

          {/* Active Trainings */}
          <StatsCard
            name="Formations Actives"
            value={stats?.trainingStats.active || 0}
            change="+8%"
            changeType="positive"
            icon="M13 10V3L4 14h7v7l9-11h-7z"
            color="emerald"
          />

          {/* Completed Trainings */}
          <StatsCard
            name="Formations Terminées"
            value={stats?.trainingStats.completed || 0}
            change="+15%"
            changeType="positive"
            icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            color="purple"
          />

          {/* Total Views */}
          <StatsCard
            name="Vues Total"
            value={stats?.totalViews || 0}
            icon="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            color="pink"
          />

          {/* Average Rating */}
          <StatsCard
            name="Note Moyenne"
            value={stats?.averageRating ? stats.averageRating.toFixed(1) : '0.0'}
            icon="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            color="orange"
          />

          {/* Total Reviews */}
          <StatsCard
            name="Avis Total"
            value={stats?.totalReviews || 0}
            icon="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            color="blue"
          />

          {/* Total Revenue */}
          <StatsCard
            name="Revenus Total"
            value={`${stats?.totalRevenue ? (stats.totalRevenue / 1000).toFixed(0) : 0}K DZD`}
            icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            color="emerald"
          />

          {/* Active Sessions */}
          <StatsCard
            name="Sessions Actives"
            value={stats?.activeSessions || 0}
            icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            color="purple"
          />

          {/* Completed Sessions */}
          <StatsCard
            name="Sessions Terminées"
            value={stats?.completedSessions || 0}
            icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            color="purple"
          />

          {/* Pending Approvals */}
          <StatsCard
            name="En Attente"
            value={stats?.pendingApprovals || 0}
            icon="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            color="orange"
          />
        </div>
      </div>
    </div>
  );
};

export default StatsPage;
